import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  type NavigationExtras,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AuthUserService } from './auth-user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthUserGuard {
  private authUserService = inject(AuthUserService);
  private router = inject(Router);

  constructor() {
    this.authUserService.authInit();
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.authUserService.userOrgObj.pipe(
      take(1),
      map((user) => {
        const pathOrgId = next.params.orgId;
        const userOrgId = user?.org._id;

        if (!pathOrgId && userOrgId) {
          return true;
        }

        if (!userOrgId || userOrgId !== pathOrgId) {
          // If the orgId is set in the path try to see if there are any
          // existing logged in users that can be switched to
          if (pathOrgId) {
            try {
              const matchingUser = this.authUserService.getAuthUserOrgObj({
                orgId: pathOrgId,
              });
              this.authUserService.selectUser(matchingUser.user._id);
              return true; // Navigation goes ahead after user selection
            } catch (err) {
              // Redirect below
            }
          }

          const navigationExtras: NavigationExtras = {
            queryParams: { redirect: state.url },
          };
          const loggedIn = !!this.authUserService.getAccount();
          if (loggedIn) {
            // If the user is logged in, send to the "maybe log in" page
            this.router.navigate(['/no-access'], navigationExtras);
            return false;
          } else {
            // If the user is logged out, send to the "log in" page
            this.router.navigate(['/login'], navigationExtras);
            return false;
          }
        }

        return true;
      })
    );
  }
}
